// Characters that never belong to an email address, so a paste splits on them
// even when the user forgot to type a separator.
export const PASTE_SPLIT_REGEX = /[\s,;]+/;

/** Splits pasted text into non-empty candidate values. */
export const splitPastedText = (text: string): string[] =>
    text.split(PASTE_SPLIT_REGEX).filter((piece) => piece !== "");

interface CommitCandidatesArgs {
    values: readonly string[];
    candidates: readonly string[];
    normalizeValue?: (value: string) => string;
    isValidValue?: (value: string) => boolean;
}

interface CommitCandidatesResult {
    values: string[];
    invalidValues: string[];
    hasChanged: boolean;
}

/**
 * The TagsInput commit pipeline: trim, drop empty, normalize, split valid from
 * invalid, then append new valid values after the existing ones.
 */
export const commitCandidates = ({
    values,
    candidates,
    normalizeValue,
    isValidValue,
}: CommitCandidatesArgs): CommitCandidatesResult => {
    const normalizedCandidates = candidates
        .map((candidate) => candidate.trim())
        .filter((candidate) => candidate !== "")
        .map((candidate) => normalizeValue?.(candidate) ?? candidate);
    const invalidValues = normalizedCandidates.filter(
        (candidate) => isValidValue !== undefined && !isValidValue(candidate),
    );
    const addedValues = [
        ...new Set(
            normalizedCandidates.filter(
                (candidate) =>
                    !invalidValues.includes(candidate) &&
                    !values.includes(candidate),
            ),
        ),
    ];

    return {
        values: [...values, ...addedValues],
        invalidValues,
        hasChanged: addedValues.length > 0,
    };
};
